import dayjs from "dayjs";
import { fetchActualComplianceAudit } from "../../../../home/schedule_components/useActualComplianceAudit";

export function rangeError(start, end) {
  if (!start?.isValid() || !end?.isValid()) return "Choose valid start and end dates.";
  if (end.isBefore(start, "day")) return "End date must be on or after start date.";
  if (end.startOf("day").diff(start.startOf("day"), "day") >= 366) return "Choose up to 366 days at a time.";
  return null;
}

// One finding, not one punch, is the count grain. Do not deduplicate by
// employee/date/type: distinct findings can legitimately share all three.
export function buildFlagReport(results) {
  const details = [];
  const types = new Map();
  const skipped = [];
  const summary = results.map(({ store, audit }) => {
    const counts = {};
    audit.groups.forEach((group) => {
      types.set(group.regulation_type, group.regulation_type_name || group.regulation_type);
      counts[group.regulation_type] = (counts[group.regulation_type] || 0) + group.violations.length;
      group.violations.forEach((v) => details.push({
        location: store.store_name,
        storeId: store.id,
        date: v.date,
        employee: v.employee_name || "Unknown employee",
        type: group.regulation_type_name || group.regulation_type,
        detail: v.violation_detail || "",
        citation: group.legal_citation || "",
      }));
    });
    audit.skipped_regulation_types.forEach((rule) => skipped.push({
      location: store.store_name, type: rule.regulation_type_name || rule.regulation_type,
    }));
    return { storeId: store.id, location: store.store_name, counts, total: Object.values(counts).reduce((a, b) => a + b, 0) };
  });
  details.sort((a, b) => a.location.localeCompare(b.location) || a.date.localeCompare(b.date) || a.employee.localeCompare(b.employee) || a.type.localeCompare(b.type));
  return { summary, details, types: [...types].sort((a, b) => a[1].localeCompare(b[1])), skipped, total: details.length };
}

// Sequential store/pay-period requests bound server concurrency and support
// a full year without a single large synchronous backend operation. No
// partial report is returned if any store/period fails.
export async function loadFlagReport(stores, start, end, { isCancelled, onProgress }) {
  const results = [];
  for (const [index, store] of stores.entries()) {
    if (isCancelled()) throw new Error("Audit cancelled");
    onProgress(`Checking ${store.store_name} (${index + 1} of ${stores.length})…`);
    const audit = await fetchActualComplianceAudit(store.id, start, end, {
      isCancelled,
      timeoutMs: 60000,
      onPeriod: (through) => onProgress(`Checking ${store.store_name} (${index + 1} of ${stores.length}): through ${dayjs(through).format("MMM D, YYYY")}`),
    });
    results.push({ store, audit });
  }
  return buildFlagReport(results);
}

export function flagExportRows(data, view) {
  if (view === "summary") return data.summary.map((row) => ({
    Location: row.location,
    ...Object.fromEntries(data.types.map(([id, label]) => [label, row.counts[id] || 0])),
    "Total flags": row.total,
  }));
  return data.details.map((row) => ({ Location: row.location, Date: row.date, Employee: row.employee, "Flag type": row.type, Description: row.detail, Citation: row.citation }));
}
